import { useCallback, useRef } from 'react';

/* eslint-disable import/order -- CI lints `-api` before `/` (correct ASCII), local eslint --fix reverses it; disable to keep CI happy. */
import type { ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import { useMutation } from '@atlassian/conversation-assistant-service/common/utils/use-mutation';
/* eslint-enable import/order */

import { useSpaces, useSpaceStoreActions } from '../../controllers/space-store/hooks';
import type { Space, UpdateSpaceRequest } from '../../controllers/space-store/types';
import { updateSpaceFetchAction } from '../useUpdateSpace/useUpdateSpace.fetch';

type UseRenameSpaceProps = {
	cloudId: string;
	product: string;
	experienceId: string;
	onSuccess?: (space: Space, newName: string) => void;
};

type RenamePayload = { spaceId: string; newName: string };

export const useRenameSpace = ({
	cloudId,
	product,
	experienceId,
	onSuccess,
}: UseRenameSpaceProps): {
	renameSpace: (spaceId: string, newName: string) => Promise<void>;
} => {
	const spaces = useSpaces();
	const { patchSpace } = useSpaceStoreActions();

	// Stable refs so neither `spaces` nor the consumer-supplied `onSuccess`
	// becomes a `useCallback` dependency that thrashes the returned
	// `renameSpace` identity.
	const spacesRef = useRef<Space[]>(spaces);
	spacesRef.current = spaces;
	const onSuccessRef = useRef(onSuccess);
	onSuccessRef.current = onSuccess;

	// Snapshot of pre-rename names keyed by spaceId. Used by `onError` to
	// roll back the optimistic patch — `useMutation`'s `onError` exposes the
	// original `payload`, so we look up the snapshot by `spaceId` and
	// restore. The ref is necessary because the snapshot is captured inside
	// `mutationFn` (where the optimistic update happens) and consumed in
	// `onError`, which runs in a separate microtask.
	const previousNamesRef = useRef<Map<string, string>>(new Map());

	const { mutate } = useMutation<RenamePayload, void>({
		mutationFn: async ({ spaceId, newName }) => {
			const currentSpace = spacesRef.current.find((s) => s.id === spaceId);
			if (!currentSpace) {
				return;
			}

			previousNamesRef.current.set(spaceId, currentSpace.name);

			// Optimistically update the name (only) in the store. patchSpace
			// also mirrors the change into the detail slice if a detail entry
			// exists, so any open ManageSpaceModal stays consistent.
			patchSpace(spaceId, { name: newName });

			const request: UpdateSpaceRequest = {
				name: newName,
				description: currentSpace.description ?? undefined,
				emoji: currentSpace.emoji,
				threadId: currentSpace.threadId,
			};

			await updateSpaceFetchAction({
				spaceId,
				request,
				cloudId,
				product,
				experienceId,
			});

			// Successful: clear snapshot and notify caller. Mirrors the prior
			// hook's behaviour of fanning out an `onSuccess(space, newName)`.
			previousNamesRef.current.delete(spaceId);
			onSuccessRef.current?.({ ...currentSpace, name: newName }, newName);
		},
		onError: (_error: ApiError, { spaceId }) => {
			// Roll back the optimistic name change on failure. We deliberately
			// only undo if a snapshot exists for this spaceId — otherwise the
			// mutation aborted before the optimistic update happened (or the
			// snapshot was already consumed by a successful retry).
			const previousName = previousNamesRef.current.get(spaceId);
			if (previousName !== undefined) {
				patchSpace(spaceId, { name: previousName });
				previousNamesRef.current.delete(spaceId);
			}
		},
	});

	const renameSpace = useCallback(
		async (spaceId: string, newName: string): Promise<void> => {
			await mutate({ spaceId, newName });
		},
		[mutate],
	);

	return { renameSpace };
};
